import authFetch from './fetch';
import qs from 'qs';
import { base64ToArrayBuffer } from 'shared/../helpers/base64ToArrayBuffer';
import { makeRequest, getQueryParams } from './utils';
import { CacheConfig } from './consts';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import React from 'react';
const QUOTES_PATH = '/api/Quotes';

export const routes = {
  fuelCalculationAppliesOnPath: () => '/api/Quotes/FuelCalculationAppliesOn',
  opportunityTypesPath: () => '/api/Quotes/OpportunityTypes',
  similarQuotesPath: () => '/api/Quotes/Similar',
  exactQuotesPath: () => '/api/Quotes/Exact',
  fuelRatesPath: () => '/api/FuelRates',
  fuelSurchargesPath: () => '/api/FuelRates/Surcharges',
  previewPath: () => '/api/FuelRates/Preview',
  fuelRatesGraphPath: () => '/api/FuelRates/Graph',
  quotesFuelRatesForDatePath: params => `/api/Quotes/FuelRate${getQueryParams(params)}`,
  fuelRatesForDatePath: (date) => `/api/FuelRates/ForDate?query=${date}`,
  fuelMostRecentRatePath: () => '/api/FuelRates/recent',
  fuelIndexNamesLookupPath: () => '/api/FuelRates/IndexNamesLookup',
  applicableDriverFscsPath: () => '/api/FuelRates/ApplicableDriverFscs',
  soldByTypesPath: () => '/api/Quotes/SoldByTypes',
  lanesLookupPath: (request) => `/api/Quotes/LanesLookup${getQueryParams(request)}`,
  billedElsewhereReasonsPath: () => '/api/Quotes/BilledElsewhereReasons',
  pricingDashboardPath: () => '/api/PricingDashboard',
  pricingDashboardDetailsPath: (details) => `/api/PricingDashboard/${details}/details`,
  salesForceIdLookupPath: () => '/api/Quotes/Lanes/SalesforceIdsLookup',
  accessorialChargeRecommendationsPath: () => '/api/PricingDashboard/accessorial-charge-recommendations',
  accessorialChargeRecommendationsCountPath: () => '/api/PricingDashboard/accessorial-charge-recommendations/count',
  accessorialChargeRecommendationsIgnorePath: (id) => `/api/PricingDashboard/accessorial-charge-recommendations/${id}/ignore`,
};

export const getSimilarQuotes = (quote) => authFetch
  .patch(routes.similarQuotesPath(), quote)
  .then(({ data }) => data)
  .catch((error) => {
    console.error(error);
    throw error;
  });

export const getExactQuotes = (quote) => authFetch
  .patch(routes.exactQuotesPath(), quote)
  .then(({ data }) => data)
  .catch((error) => {
    console.error(error);
    throw error;
  });

export const getQuoteTypes = () => makeRequest(authFetch.get(`${QUOTES_PATH}/QuoteTypes`));
export const useGetQuoteTypes = () => useQuery(['getQuoteTypes'], getQuoteTypes, CacheConfig.forever);

export const getQuoteStorageTypes = () => makeRequest(authFetch.get(`${QUOTES_PATH}/StorageTypes`));
export const useGetQuoteStorageTypes = () => useQuery(['getQuoteStorageTypes'], getQuoteStorageTypes, CacheConfig.forever);

export const getAccountTypes = () => makeRequest(authFetch.get(`${QUOTES_PATH}/AccountTypes`));
export const useGetAccountTypes = () => useQuery(['getAccountTypes'], getAccountTypes, CacheConfig.forever);

export const getDashboardData = () => authFetch
  .get(routes.pricingDashboardPath())
  .then(({ data }) => data)
  .catch((error) => {
    console.error(error);
    throw error;
  });

export const getDashboardDetailsData = (details) => authFetch
  .get(routes.pricingDashboardDetailsPath(details))
  .then(({ data }) => data)
  .catch((error) => {
    console.error(error);
    throw error;
  });

export const getOpportunityTypes = () => makeRequest(authFetch.get(`${QUOTES_PATH}/OpportunityTypes`));
export const useGetOpportunityTypes = () => useQuery(['getOpportunityTypes'], getOpportunityTypes, CacheConfig.forever);

export const getQuotesGrid = async (params = {}) => {
  try {
    // convert params { status: [], BillToId: 0, LaneRateId: 0, ExcludeExpiredQuotes: true } into
    // query string ?Status=[]&BillToId=0&LaneRateId=0&ExcludeExpiredQuotes=true
    const response = await authFetch.get(`/api/Quotes/Grid${Object.keys(params).length > 0 ? `?${qs.stringify(params)}` : ''}`);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getLanesGrid = async (params = {}) => {
  try {
    // convert params { status: [], BillToId: 0, LaneRateId: 0, ExcludeExpiredQuotes: true } into
    // query string ?Status=[]&BillToId=0&LaneRateId=0&ExcludeExpiredQuotes=true
    const response = await authFetch.get(`/api/Quotes/Lanes/Grid${Object.keys(params).length > 0 ? `?${qs.stringify(params)}` : ''}`);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getRevisionsFromParentQuoteId = async (parentQuoteId) => {
  try {
    const response = await authFetch.get(`/api/Quotes/Versions?ParentQuoteId=${parentQuoteId}`);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getQuoteByQuoteId = async (id) => {
  try {
    const response = await authFetch.get(`/api/Quotes/${id}`);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getQuoteByQuoteRevision = async (id, revision) => {
  try {
    const response = await authFetch.get(`/api/Quotes/${id}/${revision}`);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const postQuotes = async (quote) => {
  try {
    const response = await authFetch.put('/api/Quotes', quote);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const createQuoteVersion = async (quote) => {
  try {
    const response = await authFetch.put('/api/Quotes/CreateVersion', quote);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const createRevision = async (quote) => {
  try {
    const response = await authFetch.post('/api/Quotes/revision', quote);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getFuelSurchargeTypes = async () => {
  try {
    const response = await authFetch.get('/api/Quotes/FuelSurchargeTypes');
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getQuoteFuelSurchargeCalculationTypes = async () => {
  try {
    const response = await authFetch.get('/api/Quotes/QuoteFuelSurchargeCalculationTypes');
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getFuelRateIndices = (params = {}) => makeRequest(
  authFetch.get(`${routes.fuelRatesPath()}${getQueryParams({ ...params, pageSize: 1000 })}`),
);

export const getMostRecentFuelRate = () => makeRequest(
  authFetch.get(`${routes.fuelMostRecentRatePath()}`),
);

export const getFuelIndexNamesLookup = () => makeRequest(
  authFetch.get(`${routes.fuelIndexNamesLookupPath()}`),
);

export const getApplicableDriverFscs = () => makeRequest(
  authFetch.get(`${routes.applicableDriverFscsPath()}`),
);

export const getFuelSurcharges = (params) => makeRequest(
  authFetch.get(`${routes.fuelSurchargesPath()}${getQueryParams(params)}`),
);

export const getPreview = (file) => makeRequest(
  authFetch.post(`${routes.previewPath()}`, file),
);

export const upsertFuelSurcharges = (data) => makeRequest(
  authFetch.put(`${routes.fuelSurchargesPath()}`, data),
);

export const getFuelRateGraph = () => makeRequest(
  authFetch.get(routes.fuelRatesGraphPath()),
);

export const getFuelRateIndex = (date) => {
  const formatted = moment(date).format('YYYY-MM-DD');
  const urlSafeDate = encodeURIComponent(formatted);
  return makeRequest(
    authFetch.get(routes.fuelRatesForDatePath(urlSafeDate)),
  );
};

export const getQuotesFuelRates = ({date, ...params}) => {
  const formatted = moment(date).format('YYYY-MM-DD');
  const urlSafeDate = encodeURIComponent(formatted);
  return makeRequest(
    authFetch.get(routes.quotesFuelRatesForDatePath({...params, date: urlSafeDate})),
  );
};

export const useGetFuelRateIndex = (date) => {
  console.log('useGetFuelRateIndex');
  const formatted = moment(date).format('YYYY-MM-DD');
  const urlSafeDate = encodeURIComponent(formatted);
  return useQuery(['getFuelRateIndex', urlSafeDate], () => getFuelRateIndex(date));
};

export const useGetQuoteFuelRates = (params, options) => {
  const queryfn = React.useCallback(() => getQuotesFuelRates(params), [params, options]);
    return useQuery([routes.quotesFuelRatesForDatePath(params)], queryfn, {...CacheConfig.forever,...options });
}
export const getInvoiceLineItemTypesUrl = (id, marketId) => `/api/InvoiceLineItemTypes${id ? `?IDs=${id}` : ''}${marketId ? `${id ? '&' : '?'}marketId=${marketId}` : ''}`;

export const getInvoiceLineItemTypes = async (id, marketId) => {
  try {
    const response = await authFetch.get(getInvoiceLineItemTypesUrl(id, marketId));
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const useGetInvoiceLineItemTypes = (id, marketId) => useQuery([getInvoiceLineItemTypesUrl(id, marketId)], () => getInvoiceLineItemTypes(id, marketId), CacheConfig.forever);

export const getInvoiceLineItemTypesPaginated = async (params) => {
  try {
    const query = params && Object.keys(params).length > 0 ? `?${qs.stringify(params, { indices: false })}` : '';
    const response = await authFetch.get(`/api/InvoiceLineItemTypes/Grid${query}`);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getAdditionalCostRatesPaginated = async (id, params) => {
  try {
    const query = params && Object.keys(params).length > 0 ? `?${qs.stringify(params, { indices: false })}` : '';
    const response = await authFetch.get(`/api/InvoiceLineItemTypes/${id}/AdditionalChargeRates${query}`);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const postInvoiceLineItemTypes = async (data) => {
  try {
    const response = await authFetch.post('/api/InvoiceLineItemTypes', data);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const upsertAdditionalCostRate = async (id, data) => {
  try {
    const response = await authFetch.post(`/api/InvoiceLineItemTypes/${id}/AdditionalChargeRates`, data);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getInvoiceLineItemType = (id) => makeRequest(authFetch.get(`/api/InvoiceLineItemTypes/${id}`));
export const deleteInvoiceLineItemType = (id) => makeRequest(authFetch.delete(`/api/InvoiceLineItemTypes/${id}`));
export const deleteAdditionalCostRate = (id) => makeRequest(authFetch.delete(`/api/InvoiceLineItemTypes/AdditionalChargeRates/${id}`));
export const restoreInvoiceLineItemType = (id) => makeRequest(authFetch.patch(`/api/InvoiceLineItemTypes/${id}/Restore`));

export const getChargeCalculationTypes = async () => {
  try {
    const response = await authFetch.get('/api/InvoiceLineItemTypes/CalculationType');
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getInvoiceLineItemTypeUnits = async () => makeRequest(authFetch.get('/api/InvoiceLineItemTypes/Units'));
export const getInvoiceLineItemTypeGlTypes = async () => makeRequest(authFetch.get('/api/InvoiceLineItemTypes/GlTypes'));
export const getInvoiceLineItemTypeBillingRequirements = async () => makeRequest(authFetch.get('/api/InvoiceLineItemTypes/BillingRequirements'));
export const getInvoiceLineItemTypeWaitingTimeRate = async () => makeRequest(authFetch.get('/api/InvoiceLineItemTypes/WaitingTime'));

export const getQuoteStatuses = () => makeRequest(authFetch.get('/api/Quotes/QuoteStatuses'));
export const useGetQuoteStatuses = () => useQuery(['getQuoteStatuses'], getQuoteStatuses, CacheConfig.forever);

export const getQuoteStatusVersionFilters = async () => {
  try {
    const response = await authFetch.get('/api/Quotes/QuoteStatusVersionFilters');
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getQuotePDF = async (id) => {
  const hostname = window.location.origin;
  try {
    const response = await authFetch.get(`api/PDF/Quote?hostname=${hostname}&id=${id}`);
    return Promise.resolve(base64ToArrayBuffer(response.data));
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const emailQuotePDF = (id, recipient) => {
  const hostname = window.location.origin;
  return makeRequest(authFetch.post(`/api/PDF/EmailQuote?hostname=${hostname}&id=${id}&recipient=${recipient}`));
};

export const sendQuotePDFByEmail = async (id, recipient) => {
  const hostname = window.location.origin;
  try {
    const response = await authFetch.get(`api/PDF/Quote/Email?hostname=${hostname}&id=${id}&recipient=${recipient}`);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getGetTariffFSC = async (params) => {
  try {
    const response = await authFetch.get(`/api/Quotes/GetTariffFSC/${params}`);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getTotalsByStatus = async () => {
  try {
    const response = await authFetch.get('/api/Quotes/TotalsByStatus');
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getQuoteTripTypes = () => makeRequest(authFetch.get(`${QUOTES_PATH}/QuoteTripType`));
export const useGetQuoteTripTypes = () => useQuery(['getQuoteTripTypes'], getQuoteTripTypes, CacheConfig.forever);

export const getQuoteProfitMargin = async (quote) => {
  try {
    const response = await authFetch.post('/api/Quotes/ProfitMargin', quote);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getInvoiceLineItemTypeInvoiceStyles = async () => {
  try {
    const response = await authFetch.get('/api/InvoiceLineItemTypes/AdditionalChargeInvoiceStyles');
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const useGetInvoiceLineItemTypeInvoiceStyles = () => useQuery({
  queryKey: ['getInvoiceLineItemTypeInvoiceStyles'],
  queryFn: () => getInvoiceLineItemTypeInvoiceStyles(),
  cacheTime: Infinity,
  staleTime: Infinity,
});

export const getFuelSurchargeCalculationTypes = async () => {
  try {
    const response = await authFetch.get('/api/Quotes/FuelSurchargeCalculationTypes');
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const validateLocationAndLaneRate = async (params = {}) => {
  try {
    // convert params { LocationId: 27, QuoteLaneRateId: 3 } into
    // query string ?locationId=27&QuoteLaneRateId=3
    const response = await authFetch.get(
      `/api/Quotes/ValidateLocationAndLaneRate${Object.keys(params).length > 0 ? `?${qs.stringify(params)}` : ''}`,
    );
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getRequestStatuses = async () => {
  try {
    const response = await authFetch.get('/api/QuoteRequests/RequestStatuses');
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getQuoteExpirationFilters = async () => {
  try {
    const response = await authFetch.get('/api/Quotes/QuoteExpirationFilters');
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getQuotesDashboard = async () => {
  try {
    const response = await authFetch.get('/api/Quotes/Dashboard');
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getRequiredFileCategories = () => makeRequest(authFetch.get(`${QUOTES_PATH}/RequiredFileCategories`));
export const useGetRequiredFileCategories = () => useQuery(['getRequiredFileCategories'], getRequiredFileCategories, CacheConfig.forever);

export const getQuotePendingOrders = async (quoteId, quoteLaneRateId) => {
  try {
    const response = await authFetch.get(`/api/Quotes/${quoteId}/PendingOrders?QuoteLaneRateId=${quoteLaneRateId}`);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const updateQuotePendingOrders = async (data) => {
  try {
    const response = await authFetch.put(`/api/Quotes/${data.quoteId}/PendingOrders`, data);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getDeliveryAreaTypes = async () => {
  try {
    const response = await authFetch.get('/api/Quotes/DeliveryAreaTypes');
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getQuoteFiles = (id, params) => makeRequest(
  authFetch.get(`/api/Quotes/${id}/Files${getQueryParams(params)}`),
);

export const postQuoteFiles = async (quoteId, files) => {
  try {
    const response = await authFetch.post(`/api/Quotes/${quoteId}/Files`, files);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const updateQuoteFileInfo = async (quoteId, fileId, info) => {
  try {
    const response = await authFetch.put(`/api/Quotes/${quoteId}/Files/${fileId}`, info);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const deleteQuoteFile = (quoteId, fileId) => makeRequest(
  authFetch.delete(`/api/Quotes/${quoteId}/Files/${fileId}`),
);

export const getQuoteDocumentTypes = () => makeRequest(
  authFetch.get('/api/Quotes/DocumentTypes'),
);

export const getQuoteNotes = (id, params) => makeRequest(authFetch.get(`/api/Quotes/${id}/notes${getQueryParams(params)}`));
export const patchQuoteNote = (id, note) => makeRequest(authFetch.patch(`/api/Quotes/${id}/notes`, note));

export const getDirectStandards = (params) => makeRequest(authFetch.get(`${'api/DirectStandards'}${getQueryParams(params)}`));
export const getDirectStandardById = (id) => makeRequest(authFetch.get(`api/DirectStandards/${id}`));
export const getDirectStandardFromTerminal = (id) => makeRequest(authFetch.get(`api/DirectStandards/Terminal/${id}`));
export const postDirectStandard = (carrier) => makeRequest(authFetch.put('api/DirectStandards', carrier));
export const deleteDirectStandard = (id) => makeRequest(authFetch.delete(`api/DirectStandards/${id}`));
export const restoreDirectStandard = (id) => makeRequest(authFetch.patch(`api/DirectStandards/${id}/restore`));
export const getDirectStandardTypes = () => makeRequest(authFetch.get(`${'api/DirectStandards/Types'}`));
export const getDirectStandardsAudits = (id, params) => makeRequest(authFetch.get(`/api/DirectStandards/${id}/Audits${getQueryParams(params)}`));
export const getDirectStandardsAuditProperties = (id) => makeRequest(authFetch.get(`/api/DirectStandards/${id}/Audits/Properties`));
export const getDirectStandardsAuditUsers = (id) => makeRequest(authFetch.get(`/api/DirectStandards/${id}/Audits/Users`));
export const getDirectStandardForQuote = (params) => makeRequest(authFetch.get(`${'api/DirectStandards/StandardForQuote'}${getQueryParams(params)}`));

export const getFuelLaneRateTypes = () => makeRequest(authFetch.get(`${QUOTES_PATH}/FuelLaneRateTypes`));
export const useGetFuelLaneRateTypes = () => useQuery(['getFuelLaneRateTypes'], getFuelLaneRateTypes, CacheConfig.forever);

export const getQuoteDriverPayCost = (request) => makeRequest(authFetch.post(`${'/api/Quotes/DriverPay'}`, request));
export const getChassisInvoiceLineItemType = () => makeRequest(authFetch.get(`${'/api/InvoiceLineItemTypes/Chassis'}`));

export const getChassisLaneRateTypes = () => makeRequest(authFetch.get(`${QUOTES_PATH}/ChassisLaneRateTypes`));
export const useGetChassisLaneRateTypes = () => useQuery(['getChassisLaneRateTypes'], getChassisLaneRateTypes, CacheConfig.forever);

export const getChassisCustomerAgreementTypes = () => makeRequest(authFetch.get('/api/Quotes/ChassisCustomerAgreementTypes'));

export const getQuoteDeliveryTypes = () => makeRequest(authFetch.get('/api/Quotes/DeliveryTypes'));
export const useGetQuoteDeliveryTypes = () => useQuery(['getQuoteDeliveryTypes'], getQuoteDeliveryTypes, CacheConfig.forever);

export const getFuelLaneInvoiceStyles = () => makeRequest(authFetch.get('/api/Quotes/FuelLaneInvoiceStyles'));
export const useGetFuelLaneInvoiceStyles = () => useQuery(['getFuelLaneInvoiceStyles'], getFuelLaneInvoiceStyles, CacheConfig.forever);

export const getQuoteRateApplianceMethods = () => makeRequest(authFetch.get('/api/Quotes/QuoteRateApplianceMethods'));
export const useGetQuoteRateApplianceMethods = () => useQuery(['getQuoteRateApplianceMethods'], getQuoteRateApplianceMethods, CacheConfig.forever);

export const getFuelCalculationAppliesOn = () => makeRequest(authFetch.get(routes.fuelCalculationAppliesOnPath()));
export const useGetFuelCalculationAppliesOn = () => useQuery(['getFuelCalculationAppliesOn'], getFuelCalculationAppliesOn, CacheConfig.forever);

export const getQuoteLaneSoldByTypes = () => makeRequest(authFetch.get(routes.soldByTypesPath()));
export const useGetQuoteLaneSoldByTypes = () => useQuery(['getQuoteLaneSoldByTypes'], getQuoteLaneSoldByTypes, CacheConfig.forever);
export const getQuoteLanesLookup = (params) => makeRequest(authFetch.get(routes.lanesLookupPath(params)));
export const useGetQuoteLanesLookup = (params) => useQuery([routes.lanesLookupPath(params)], () => getQuoteLanesLookup(params));

export const getChassisBilledElsewhereReasons = () => makeRequest(authFetch.get(routes.billedElsewhereReasonsPath()));
export const useGetChassisBilledElsewhereReasons = () => useQuery(['getChassisBilledElsewhereReasons'], getChassisBilledElsewhereReasons, CacheConfig.forever);

export const getLaneAdditionalChargeRateTypes = () => makeRequest(authFetch.get(`${QUOTES_PATH}/AdditionalChargeRateTypes`));
export const useGetLaneAdditionalChargeRateTypes = () => useQuery(['getLaneAdditionalChargeRateTypes'], getLaneAdditionalChargeRateTypes, CacheConfig.forever);

export const getQuoteInvoicePreferences = (id) => makeRequest(authFetch.get(`/api/Quotes/${id}/InvoicePreferences`));
export const upsertQuoteInvoicePreferences = (id, data) => makeRequest(authFetch.put(`/api/Quotes/${id}/InvoicePreferences`, data));
export const deleteQuoteInvoicePreference = (id, invoicePreferenceId) => makeRequest(authFetch.delete(`/api/Quotes/${id}/InvoicePreferences/${invoicePreferenceId}`));

export const getQuoteInvoicePreferenceDetailOptions = () => makeRequest(authFetch.get(`${QUOTES_PATH}/InvoicePreferenceDetailOptions`));
export const useGetQuoteInvoicePreferenceDetailOptions = () => useQuery(['getQuoteInvoicePreferenceDetailOptions'], getQuoteInvoicePreferenceDetailOptions, CacheConfig.forever);

export const getQuoteInvoicePreferenceBillToOptions = () => makeRequest(authFetch.get(`${QUOTES_PATH}/InvoicePreferenceBillToOptions`));
export const useGetQuoteInvoicePreferenceBillToOptions = () => useQuery(['getQuoteInvoicePreferenceBillToOptions'], getQuoteInvoicePreferenceBillToOptions, CacheConfig.forever);

// Rate uploader
const RATEUPLOADER_PATH = `${QUOTES_PATH}/RateUpload`;

export const getRatesUploaderTemplateFile = () => makeRequest(authFetch.get(RATEUPLOADER_PATH));
export const uploadRatesUploaderFile = (fileData) => makeRequest(authFetch.post(RATEUPLOADER_PATH, fileData));

// Quick rate
const QUICK_RATE_PATH = `${QUOTES_PATH}/QuickRate`;

export const getQuickRate = (params) => makeRequest(authFetch.post(QUICK_RATE_PATH, params));
export const getQuickRateHistory = () => makeRequest(authFetch.get(`${QUICK_RATE_PATH}/History`));

export const getSalesForceIdsFromQuoteLanes = async ({ search }) => {
  const queryParams = new URLSearchParams({ search: search?.name ?? search }).toString();
  return makeRequest(authFetch.get(`${routes.salesForceIdLookupPath()}?${queryParams}`));
};

export const getAccessorialChargeRecommendations = (params) => makeRequest(authFetch.get(`${routes.accessorialChargeRecommendationsPath()}${getQueryParams(params)}`));

export const getAccessorialChargeRecommendationsCount = () => makeRequest(authFetch.get(routes.accessorialChargeRecommendationsCountPath()));
export const useGetAccessorialChargeRecommendationsCount = () => useQuery(['getAccessorialChargeRecommendationsCount'], getAccessorialChargeRecommendationsCount, CacheConfig.never);

export const ignoreAccessorialChargeRecommendation = (id) => makeRequest(authFetch.put(routes.accessorialChargeRecommendationsIgnorePath(id)));
